import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, Github, Loader2, Plus, Search } from 'lucide-react';
import { addCodebase, listGithubRepos } from '@/lib/api';
import type { CodebaseResponse, GithubRepoPickerItem } from '@/lib/api';
import { filterGithubRepos, getGithubRepoPickerStatusLabel } from '@/lib/github-codebases';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';

interface GithubRepoPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdded: (codebase: CodebaseResponse) => void;
}

function getRepoStatusVariant(
  status: GithubRepoPickerItem['pickerStatus']
): 'default' | 'secondary' | 'outline' {
  if (status === 'can_connect') {
    return 'default';
  }

  if (status === 'needs_github_app_access') {
    return 'outline';
  }

  return 'secondary';
}

export function GithubRepoPickerDialog({
  open,
  onOpenChange,
  onAdded,
}: GithubRepoPickerDialogProps): React.ReactElement {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [addingRepoId, setAddingRepoId] = useState<string | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const {
    data: repos,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['github-repos'],
    queryFn: listGithubRepos,
    enabled: open,
    refetchInterval: open ? 30_000 : false,
  });

  const filteredRepos = useMemo(
    () => filterGithubRepos(repos ?? [], searchQuery),
    [repos, searchQuery]
  );

  useEffect(() => {
    if (!open) {
      setSearchQuery('');
      setError(null);
      setAddingRepoId(null);
      return;
    }

    searchRef.current?.focus();
  }, [open]);

  const handleSelect = async (repo: GithubRepoPickerItem): Promise<void> => {
    if (addingRepoId) {
      return;
    }

    setAddingRepoId(repo.githubRepoId);
    setError(null);

    try {
      const codebase = await addCodebase({ githubRepoId: repo.githubRepoId });
      await queryClient.invalidateQueries({ queryKey: ['codebases'] });
      onAdded(codebase);
      onOpenChange(false);
    } catch (caughtError) {
      setError(caughtError instanceof Error ? caughtError.message : 'Failed to add codebase');
    } finally {
      setAddingRepoId(null);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen): void => {
        onOpenChange(nextOpen);
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add a codebase</DialogTitle>
          <DialogDescription>
            Pick a repository from the org-scoped GitHub App list. Repositories with pending access
            can still be added, and Metis will show the install/manage link until access is
            refreshed.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-text-tertiary" />
          <Input
            ref={searchRef}
            value={searchQuery}
            onChange={(e): void => {
              setSearchQuery(e.target.value);
            }}
            placeholder="Search by repository name or status…"
            className="pl-9"
          />
        </div>

        {error && <p className="text-sm text-error">{error}</p>}

        <ScrollArea className="h-[55vh] pr-3">
          <div className="flex flex-col gap-2">
            {isLoading ? (
              <div className="flex items-center justify-center py-8 text-sm text-text-tertiary">
                Loading repositories…
              </div>
            ) : isError ? (
              <div className="flex items-center justify-center py-8 text-sm text-error">
                Failed to load repositories
              </div>
            ) : filteredRepos.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
                <Github className="size-8 text-text-tertiary" />
                <p className="text-sm text-text-secondary">No matching repositories</p>
                <p className="text-xs text-text-tertiary">
                  Try searching by full name or pending access status.
                </p>
              </div>
            ) : (
              filteredRepos.map(repo => {
                const isAdding = addingRepoId === repo.githubRepoId;
                const statusLabel = getGithubRepoPickerStatusLabel(repo);

                return (
                  <div
                    key={repo.githubRepoId}
                    className="rounded-lg border border-border bg-surface p-3"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex min-w-0 flex-wrap items-center gap-2">
                          <Github className="size-4 shrink-0 text-text-tertiary" />
                          <span className="truncate text-sm font-medium text-text-primary">
                            {repo.fullName}
                          </span>
                          {statusLabel && (
                            <Badge variant={getRepoStatusVariant(repo.pickerStatus)}>
                              {statusLabel}
                            </Badge>
                          )}
                        </div>
                        <p className="mt-1 text-xs text-text-tertiary">
                          {repo.isPrivate ? 'Private' : 'Public'} · default branch{' '}
                          {repo.defaultBranch}
                        </p>
                      </div>

                      <Button
                        type="button"
                        size="sm"
                        onClick={(): void => {
                          void handleSelect(repo);
                        }}
                        disabled={Boolean(addingRepoId) && !isAdding}
                      >
                        {isAdding ? (
                          <>
                            <Loader2 className="size-4 animate-spin" />
                            Adding…
                          </>
                        ) : (
                          <>
                            <Plus className="size-4" />
                            Add
                          </>
                        )}
                      </Button>
                    </div>

                    {repo.installUrl && (
                      <a
                        href={repo.installUrl}
                        target="_blank"
                        rel="noreferrer"
                        className={cn(
                          'mt-3 inline-flex items-center gap-1 text-xs text-primary hover:underline',
                          repo.pickerStatus === 'already_added' && 'text-text-tertiary'
                        )}
                      >
                        {repo.pickerStatus === 'needs_github_app_access'
                          ? 'Install or manage GitHub App access'
                          : 'Manage GitHub App access'}
                        <ExternalLink className="size-3.5" />
                      </a>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
